import {Object3D} from '@wonderlandengine/api';
import {
    Align,
    Display,
    FlexDirection,
    Justify,
    PositionType,
    TextProps,
} from '@wonderlandengine/react-ui';
import {
    Column,
    Container,
    Image,
    MaterialContext,
    Panel,
    Panel9Slice,
    PanelProps,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {PropsWithChildren, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {
    useRotBMaterialContext,
    RotBMaterialContextValue,
} from '../utils/rotb-material-context.js';
import {insertNewlines, parseColor} from '../utils/utils.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {FlatOpaqueTextured, UIText} from '../../types/materials.js';
import {ColoredImage} from './colored-image.js';
import {useMemoMaterial} from '../utils/MaterialPool.js';

export enum TailDirection {
    none = 'none',
    down = 'down',
    right = 'right',
    left = 'left',
}

// let SpeechTextMaterial: UIText | null = null;

export const MalSpeech = (
    props: PropsWithChildren<TextProps> & {
        textWidth?: number;
        height?: number;
        direction?: TailDirection;
    }
) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;
    const mats = useContext(MaterialContext);
    const material = mats.panelMaterialTextured;
    const shadowMaterial = rotbMats?.buttonShadowMaterial;
    const bm = useMemoMaterial('buttonSimple', () => {
        const m = material!.clone() as FlatOpaqueTextured;
        m.setFlatTexture(rotbMats?.buttonSimple);
        return m;
    });

    const speechTextMaterial = useMemoMaterial(`UIText_${colorSwatch.UiDarkText}`, () => {
        const SpeechTextMaterial = rotbMats?.regularTextMaterial?.clone() as UIText;
        SpeechTextMaterial.setColor(parseColor(colorSwatch.UiDarkText));
        return SpeechTextMaterial;
    });

    const [text, setText] = useState(props.children?.toString() ?? props.text);
    useEffect(() => {
        const t = props.children?.toString() ?? props.text;
        setText(t);
    }, [props.text, props.children]);

    return (
        <Column>
            {props.direction === TailDirection.left && (
                <ColoredImage
                    position={PositionType.Absolute}
                    z={0.001}
                    color={'#D4D4D4'}
                    top={60}
                    left={-23}
                    rounding={0}
                    width={25}
                    height={25}
                    src={rotbMats.wordBalloonTailFlipped}
                />
            )}
            <Panel9Slice
                {...props}
                // width={props.width ?? 250}
                minHeight={80}
                material={bm}
                borderSize={8}
                borderTextureSize={0.3}
                justifyContent={Justify.Center}
                alignContent={Align.Center}
                alignItems={Align.Center}
            >
                <Text
                    margin={10}
                    textAlign="center"
                    fontSize={26}
                    material={speechTextMaterial}
                    textWrap="soft"
                    text={text}
                />
            </Panel9Slice>
            {props.direction === TailDirection.down && (
                <Row>
                    <Container width="75%"></Container>
                    <ColoredImage
                        color={'#B1B1B1'}
                        top={-4}
                        rounding={0}
                        width={25}
                        height={25}
                        src={rotbMats.wordBalloonTail}
                    />
                </Row>
            )}
            {props.direction === TailDirection.right && (
                <ColoredImage
                    alignSelf={Align.FlexEnd}
                    z={0.001}
                    color={'#D4D4D4'}
                    top={-34}
                    right={-23}
                    rounding={0}
                    width={25}
                    height={25}
                    src={rotbMats.wordBalloonTailRotated}
                />
            )}
        </Column>
    );
};

MalSpeech.displayName = 'MalSpeech';
